// Shared status verdict for Org Activity (client header + server activity API).

export type StatusTone = "amber" | "green" | "gold" | "red" | "grey";

export type StatusVerdict = {
  tone: StatusTone;
  label: string;
  message: string;
  next_action_label: string;
  next_action_route: string;
};

export type ComputeStatusVerdictInput = {
  todayCount: number;
  goal: number;
  capTotal: number;
  redAfterLocalTime: string;
  nowLocalIso: string;
};

const ROUTE_INGEST = "/ads-assistant?mode=ingestion";

function parseHourMinute(value: string): { hour: number; minute: number } | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  if (!m) return null;
  return { hour: parseInt(m[1]!, 10), minute: parseInt(m[2]!, 10) };
}

function isPastCutoff(nowLocalIso: string, cutoff: string): boolean {
  const cutoffParts = parseHourMinute(cutoff);
  if (!cutoffParts) return false;
  const d = new Date(nowLocalIso);
  if (Number.isNaN(d.getTime())) return false;
  const localHour = d.getHours();
  const localMin = d.getMinutes();
  return localHour > cutoffParts.hour || (localHour === cutoffParts.hour && localMin >= cutoffParts.minute);
}

export function computeStatusVerdict(input: ComputeStatusVerdictInput): StatusVerdict {
  const { todayCount, goal, capTotal, redAfterLocalTime, nowLocalIso } = input;

  if (goal <= 0) {
    return {
      tone: "grey",
      label: "Coaching off",
      message: "Org Activity coaching is disabled for this org.",
      next_action_label: "Log an ad",
      next_action_route: ROUTE_INGEST,
    };
  }

  if (capTotal > 0 && todayCount > capTotal) {
    return {
      tone: "grey",
      label: "Well past goal",
      message: `${todayCount} ads today — past the soft cap of ${capTotal}. Mix in fresh creatives instead of more volume.`,
      next_action_label: "Open library",
      next_action_route: "/ads-assistant?mode=library",
    };
  }

  if (capTotal > 0 && todayCount === capTotal) {
    return {
      tone: "gold",
      label: "Above and beyond",
      message: `${todayCount} of ${goal} logged — at the soft cap (${capTotal}). Excellent pace.`,
      next_action_label: "Open library",
      next_action_route: "/ads-assistant?mode=library",
    };
  }

  if (todayCount > goal && capTotal > 0) {
    return {
      tone: "gold",
      label: "Above and beyond",
      message: `${todayCount} of ${goal} logged — above goal, soft cap ${capTotal}.`,
      next_action_label: "Log another",
      next_action_route: ROUTE_INGEST,
    };
  }

  if (todayCount >= goal) {
    return {
      tone: "green",
      label: "Goal met",
      message: `${todayCount} of ${goal} logged today. Nice — keep cadence.`,
      next_action_label: "Log another",
      next_action_route: ROUTE_INGEST,
    };
  }

  const past = isPastCutoff(nowLocalIso, redAfterLocalTime);
  const remaining = Math.max(0, goal - todayCount);
  if (past) {
    return {
      tone: "red",
      label: "Behind — past cutoff",
      message:
        todayCount === 0
          ? `Past ${redAfterLocalTime} with 0 of ${goal} ads logged. Run one now to recover.`
          : `Past ${redAfterLocalTime} with ${todayCount} of ${goal} logged — ${remaining} to go.`,
      next_action_label: "Log now",
      next_action_route: ROUTE_INGEST,
    };
  }

  return {
    tone: "amber",
    label: "Behind goal",
    message:
      todayCount === 0
        ? `0 of ${goal} ads logged today — start one before ${redAfterLocalTime}.`
        : `${todayCount} of ${goal} logged — ${remaining} more before ${redAfterLocalTime}.`,
    next_action_label: "Log now",
    next_action_route: ROUTE_INGEST,
  };
}
